/**
 * Interface pour le statut de traitement d'un média
 */
export interface MediaProcessingStatus {
  hash: string;
  media_url: string | null;
  byte_size: number | null;
  progress: number;
  phase: string;
}

/**
 * Options pour le polling du statut de traitement
 */
export interface MediaProcessingOptions {
  hash: string;
  pollInterval?: number;
  progressUpdateInterval?: number;
  onProgress?: (progress: number, phase: string) => void;
  onComplete?: (data: MediaProcessingStatus) => void;
  onError?: (error: Error) => void;
}

/**
 * Classe pour gérer le polling du statut de traitement d'un média
 */
export class MediaProcessingPoller {
  private hash: string;
  private pollInterval: number;
  private progressUpdateInterval: number;
  private pollTimer: number | null = null;
  private progressTimer: number | null = null;
  private currentProgress: number = 0;
  private currentPhase: string = "";
  private targetProgress: number = 0;
  private isFirstSync: boolean = true; // Flag pour la première synchronisation
  private onProgress?: (progress: number, phase: string) => void;
  private onComplete?: (data: MediaProcessingStatus) => void;
  private onError?: (error: Error) => void;

  constructor(options: MediaProcessingOptions) {
    this.hash = options.hash;
    this.pollInterval = options.pollInterval || 4000;
    this.progressUpdateInterval = options.progressUpdateInterval || 500;
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.onError = options.onError;
  }

  /**
   * Démarre le polling
   */
  start(): void {
    // Vérification initiale
    this.checkStatus();

    // Configuration de l'intervalle de polling
    this.pollTimer = window.setInterval(() => {
      this.checkStatus();
    }, this.pollInterval);

    // Configuration de l'animation de progression
    this.progressTimer = window.setInterval(() => {
      this.animateProgress();
    }, this.progressUpdateInterval);
  }

  /**
   * Arrête le polling
   */
  stop(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.progressTimer !== null) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  /**
   * Anime la progression vers la cible
   */
  private animateProgress(): void {
    if (this.currentProgress < this.targetProgress) {
      this.currentProgress++;
      if (this.onProgress) {
        this.onProgress(this.currentProgress, this.currentPhase);
      }
    }
  }

  /**
   * Vérifie le statut du traitement du média
   */
  private async checkStatus(): Promise<void> {
    const env = (import.meta as ImportMeta & { env: Record<string, string> }).env;
    try {
      const response = await fetch(
        `${env.VITE_REMOTION_SERVER_URL}/process-media-status?hash=${this.hash}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: MediaProcessingStatus = await response.json();
      console.log(data);

      const phaseChanged = data.phase !== this.currentPhase;

      if (data.phase) {
        this.currentPhase = data.phase;
      }

      if (data.phase === "failed") {
        this.currentProgress = 0;
        this.targetProgress = 0;
        if (this.onProgress) {
          this.onProgress(0, "failed");
        }
        this.stop(); // On arrête de poller si c'est mort
        return;
      }
      // Mettre à jour la progression
      if (typeof data.progress === "number") {
        if (this.isFirstSync) {
          // Première synchronisation : on se met directement à jour
          this.currentProgress = data.progress;
          this.targetProgress = data.progress;
          this.isFirstSync = false;

          if (this.onProgress) {
            this.onProgress(this.currentProgress, this.currentPhase);
          }
        } else {
          // Synchronisations suivantes : animation progressive
          this.targetProgress = data.progress;
          if (phaseChanged && this.onProgress) {
            this.onProgress(this.currentProgress, this.currentPhase);
          }
        }
      }

      // Si le média est prêt, arrêter le polling et notifier
      if (data.media_url) {
        this.currentProgress = 100;
        if (this.onProgress) {
          this.onProgress(100, this.currentPhase);
        }

        this.stop();
        if (this.onComplete) {
          this.onComplete(data);
        }
      }
    } catch (error) {
      console.error("Error checking media status:", error);
      if (this.onError && error instanceof Error) {
        this.onError(error);
      }
    }
  }
}

/**
 * Fonction utilitaire simplifiée pour démarrer le polling
 * @param options Options de configuration du polling
 * @returns Instance de MediaProcessingPoller
 */
export function startMediaProcessingPoll(options: MediaProcessingOptions): MediaProcessingPoller {
  const poller = new MediaProcessingPoller(options);
  poller.start();
  return poller;
}
